import { Box, HStack, VStack, useDisclosure } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import AugmentationLayout from "@pathwright/ux/src/components/augmentation/AugmentationLayout"
import AugmentationRoutedPanel from "@pathwright/ux/src/components/augmentation/AugmentationRoutedPanel"
import AugmentationToolbar, {
  AugmentationToolbarButton,
  AugmentationToolbarOption,
  AugmentationToolbarOptions,
  AugmentationToolbarProps,
  useAugmentationToolbar
} from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { AnimatePresence, motion } from "framer-motion"
import { forwardRef, useMemo } from "react"
import { usePrevious } from "react-use"
import useChangelogAugmentationToolbarOption from "../changelog/augmentation/useChangelogAugmentationToolbarOption"
import useIntercomAugmentationToolbar from "../intercom/augmentation/useIntercomAugmentationToolbar"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import Share from "../share/Share"
import { getManageResourceUrl } from "../utils/urls"
import { PricingButton } from "./CoverPageHeader"
import { useCoverPageContext } from "./CoverPageProvider"
import { useGetPathUrl } from "./utils"

type CoverPageToolbarPosition = "bottom" | "side"

// Shared wrapper component with common toolbar styles
function ToolbarWrapper({
  children,
  bgColor,
  ...boxProps
}: {
  children: React.ReactNode
} & React.ComponentProps<typeof Box>) {
  return (
    <Box
      sx={{
        ".AugmentationToolbarButton:hover": {
          // TODO: move to toolbar (prefer over Home Screen treatment).
          bgColor: "whiteAlpha.600"
        },
        "& *:has(> .ToolbarStack)": {
          bgColor
        }
      }}
      {...boxProps}
    >
      {children}
    </Box>
  )
}

// Rendering the toolbar based on screen size.
// - base: render at bottom of screen
// - md+: render to the right of cover page
export function useCoverPageToolbarPosition<T>(
  breakpointOptions: Record<CoverPageToolbarPosition, T>
): T {
  const isMobile = useCoverPageContext((v) => v.isMobile)
  const position = isMobile ? "bottom" : "side"
  return breakpointOptions[position]
}

// Toolbar positioned on the right side for larger screens
const CoverPageToolbarSide = forwardRef<
  HTMLDivElement,
  AugmentationToolbarProps
>(({ toolbar }, ref) => {
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  return (
    <VStack
      ref={ref}
      position="absolute"
      top={immersiveActive ? "1.5rem" : 0}
      right={immersiveActive ? "20px" : "-50px"}
      zIndex={12}
      spacing={2}
      className="CoverPageToolbar CoverPageToolbarSide"
      sx={{
        ".AugmentationToolbar": {}
      }}
    >
      <ToolbarWrapper>
        <AugmentationToolbar toolbar={toolbar} alwaysVisible spacing={1} />
      </ToolbarWrapper>
    </VStack>
  )
})

// Toolbar positioned at the bottom for smaller screens
const CoverPageToolbarBottom = forwardRef<
  HTMLDivElement,
  AugmentationToolbarProps
>(({ toolbar }, ref) => {
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)
  const toolbarBg = immersiveActive ? "whiteAlpha.300" : "blackAlpha.100"

  // For now, we won't render a separate immersive toolbar on mobile
  // since it conflicts with the Immersive navigation bar
  if (immersiveActive) return null

  return (
    <HStack
      ref={ref}
      className="CoverPageToolbar CoverPageToolbarBottom"
      position="fixed"
      bottom="10px"
      left="50%"
      spacing={2}
      transform="translateX(-50%)"
      zIndex={101}
      display="flex"
      justifyContent="center"
    >
      <ToolbarWrapper bgColor={toolbarBg}>
        <AugmentationToolbar
          toolbar={toolbar}
          alwaysVisible
          direction="row"
          spacing={1}
        />
      </ToolbarWrapper>
    </HStack>
  )
})

// Individual option components for better readability
function CloseOption({
  onClose,
  immersiveActive
}: {
  onClose: () => void
  immersiveActive?: boolean
}): AugmentationToolbarOption {
  return {
    key: "close",
    icon: "x-thick",
    label: immersiveActive ? "Back to Cover" : "Close",
    onClick: onClose
  }
}

function ManageOption({
  manageResourceUrl
}: {
  manageResourceUrl: string
}): AugmentationToolbarOption {
  return {
    key: "manage",
    icon: "gear-fill2" as IconName,
    label: "Manage",
    to: manageResourceUrl
  }
}

function ShareOption({
  sourcePathId
}: {
  sourcePathId: string | number
}): AugmentationToolbarOption {
  return {
    key: "share",
    icon: "share-fill" as IconName,
    label: "Share",
    ToolbarComponent: () => (
      <Share
        context={{
          type: "cohort",
          typeId:
            typeof sourcePathId === "string"
              ? parseInt(sourcePathId, 10)
              : sourcePathId
        }}
        hideTabs
        openWithKeys
        onToggleIsOpen={null}
        placement={null}
        triggerLabel={null}
        portal={false}
        displayType="modal"
      >
        {({ renderTrigger }) =>
          renderTrigger(({ onToggle }: { onToggle: () => void }) => (
            <AugmentationToolbarButton
              option={{
                key: "share-trigger",
                icon: "share-fill",
                label: "Share this Path",
                onClick: onToggle,
                bgColor: "transparent",
                variant: "ghost"
              }}
              isSelected={false}
            />
          ))
        }
      </Share>
    )
  }
}

function PathOption({
  pathUrl
}: {
  pathUrl: string
}): AugmentationToolbarOption {
  return {
    key: "path",
    icon: "path",
    label: "Path",
    to: pathUrl
  }
}

function ExpandedPricingButton() {
  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: "auto", opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <PricingButton
        collapsed
        leftIcon={<Pathicon icon="plus" size="1.2em" />}
        px={4}
        py={2}
        renderButton={undefined}
      />
    </motion.div>
  )
}

function CollapsedPricingButton() {
  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: "auto", opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <PricingButton
        collapsed
        leftIcon={undefined}
        px={4}
        py={3}
        renderButton={(props) => {
          const isLinkProps = "to" in props
          return (
            <AugmentationToolbarButton
              option={{
                key: "pricing-trigger",
                icon: "plus",
                label: "",
                // Pass onClick if it exists (for regular buttons only)
                ...(props.onClick &&
                  !isLinkProps && {
                    onClick:
                      props.onClick as React.MouseEventHandler<HTMLButtonElement>
                  }),
                // Pass to if it exists (for Link components)
                ...(isLinkProps && { to: props.to as string }),
                as: props.as
              }}
              isSelected={false}
            />
          )
        }}
      />
    </motion.div>
  )
}

function PricingOption({
  headerCollapsed
}: {
  headerCollapsed: boolean
}): AugmentationToolbarOption {
  return {
    key: "pricing",
    icon: "plus" as IconName,
    label: "",
    ToolbarComponent: () => (
      <Box position="relative">
        <AnimatePresence mode="wait">
          {headerCollapsed ? (
            <ExpandedPricingButton key="expanded" />
          ) : (
            <CollapsedPricingButton key="collapsed" />
          )}
        </AnimatePresence>
      </Box>
    )
  }
}

export const useToolbarOptions = () => {
  const { resource, onClose, isMobile, headerCollapsed, immersiveActive } =
    useCoverPageContext()
  const { me } = usePathwrightContext()
  const canEditLibrary =
    me?.permissions?.can_edit_library ||
    me?.permissions?.can_administrate ||
    me?.permissions?.has_pathwright_staff_access

  const sourcePathId = resource?.curriculum_group?.id
  const manageResourceUrl =
    resource?.id && canEditLibrary
      ? `${getManageResourceUrl(resource.id)}details`
      : null

  const pathUrl = useGetPathUrl()

  const changelogOption = useChangelogAugmentationToolbarOption()
  const intercomOption = useIntercomAugmentationToolbar()

  const options: AugmentationToolbarOptions = useMemo(
    () => [
      [
        onClose && !isMobile ? CloseOption({ onClose, immersiveActive }) : null,
        manageResourceUrl && !(isMobile && immersiveActive)
          ? ManageOption({ manageResourceUrl })
          : null,
        sourcePathId ? ShareOption({ sourcePathId }) : null,
        isMobile && !immersiveActive
          ? PricingOption({
              headerCollapsed: headerCollapsed || immersiveActive || false
            })
          : null
      ],
      [pathUrl ? PathOption({ pathUrl }) : null],
      [intercomOption, !isMobile && !immersiveActive ? changelogOption : null]
    ],
    [
      onClose,
      isMobile,
      manageResourceUrl,
      sourcePathId,
      headerCollapsed,
      immersiveActive,
      pathUrl,
      changelogOption,
      intercomOption
    ]
  )

  return options
}

const CoverPageAugmentationToolbar = () => {
  // Build toolbar options using extracted components
  const options: AugmentationToolbarOptions = useToolbarOptions()

  const CoverPageToolbarComponent = useCoverPageToolbarPosition({
    bottom: CoverPageToolbarBottom,
    side: CoverPageToolbarSide
  })

  // useBreakpointValue renders the base initially on the first render,
  // no matter what the real breakpoint is, so ignore the first output.
  const shouldRender = !!usePrevious(CoverPageToolbarComponent)

  const toolbar = useAugmentationToolbar(options)
  const disclosure = useDisclosure()

  // NOTE: there are some issues:
  // 1) The changelog option somehow opens 2 panels.
  // 2) The panel isn't offset to the left of the toolbar when on the side.
  return shouldRender ? (
    <AugmentationLayout
      components={{
        Panel: AugmentationRoutedPanel,
        Toolbar: CoverPageToolbarComponent
      }}
      toolbar={toolbar}
      disclosure={disclosure}
      spacing={[40, 10, 10]}
      // padding={20}
      allowOverlay
      panelPosition="far"
    />
  ) : null
}

export default CoverPageAugmentationToolbar
